import type { PrintSheetHtml, PrintSheetLayout } from '../print/templates';
import type { VisitPageAnswer } from './visitPage';
import type { WorkOrderVisitStatus } from './visits';

/**
 * 🔒 The visit's service report (piece 29; decisions 1910, 2060, 2061; T1243).
 * Complete the Visit opens the visit's completion sheet: the work done, the
 * lines worked, the parts fitted, the end time, and an optional customer's
 * comment above the customer's signature. The customer signs, then the lead
 * worker signs beside it with their typed name, and the sheet is frozen as a
 * PDF at the lead's signature. These are the sheet's rules that need no
 * database.
 */

/** 🔒 Why a customer gave no signature, printed in its place (piece 29, the completion signature). */
export const VISIT_NO_SIGNATURE_REASONS = ['NobodyOnSite', 'DeclinedToSign'] as const;
export type VisitNoSignatureReason = (typeof VISIT_NO_SIGNATURE_REASONS)[number];

export const VISIT_NO_SIGNATURE_REASON_LABELS: Readonly<Record<VisitNoSignatureReason, string>> = {
  NobodyOnSite: 'Nobody on site',
  DeclinedToSign: 'Declined to sign',
};

/** What a visit completed as; Could not complete is an outcome on a Complete visit, never a sixth state (decision 1901). */
export const VISIT_OUTCOMES = ['Completed', 'CouldNotComplete'] as const;
export type VisitOutcome = (typeof VISIT_OUTCOMES)[number];

/**
 * 🔨 A drawn signature travels as a PNG data address and is refused past this
 * many characters: about 70 kilobytes of picture, which a signature pad's
 * strokes never approach, and under the request's own size limit with two.
 */
export const SIGNATURE_IMAGE_MAX_CHARS = 90_000;
/** The pad's export, and the one shape a drawn signature arrives in. */
export const SIGNATURE_IMAGE_PREFIX = 'data:image/png;base64,';
/**
 * 🔨 What the base64 of a PNG's eight signature bytes and the first byte of its first chunk's length
 * reads as, `89 50 4E 47 0D 0A 1A 0A 00`; every PNG the pad exports starts with it (T1360).
 */
const SIGNATURE_PNG_BASE64_MAGIC = 'iVBORw0KGgo';
const BASE64_PAYLOAD = /^[A-Za-z0-9+/]+={0,2}$/;

/** One signature drawn on the screen: the signer's typed name and the drawn picture. */
export interface DrawnSignature {
  name: string;
  /** A `data:image/png;base64,` address, the pad's own export. */
  image: string;
}

/** What Complete the Visit sends: the sheet's words, the customer's signature or the reason for none, and the lead's. */
export interface CompleteVisitInput {
  outcome: VisitOutcome;
  /** The lead's words; required when the visit could not complete. */
  outcomeNote?: string | null;
  /** The work done, in the lead's words. */
  workSummary?: string | null;
  /** The customer's own words, optional, printed above their signature. */
  customerComment?: string | null;
  customerSignature?: DrawnSignature | null;
  noSignatureReason?: VisitNoSignatureReason | null;
  leadSignature: DrawnSignature;
  /** 🔒 The work performed the lead types beneath each line on the sheet, saved onto the line (decision 2061). */
  lineWork?: readonly { lineId: string; workPerformed: string | null }[];
  /** The visit's version as the page read it, so a sheet signed over a visit somebody else moved is refused. */
  version?: number | null;
}

/** 🔨 Why a drawn signature is refused, or null: a PNG from the pad (base64 whose first bytes are a PNG's), not empty, not past the ceiling. */
export function signatureImageProblem(image: string | null | undefined): string | null {
  if (!image || !image.startsWith(SIGNATURE_IMAGE_PREFIX) || image.length <= SIGNATURE_IMAGE_PREFIX.length) return 'the signature is drawn on the pad';
  if (image.length > SIGNATURE_IMAGE_MAX_CHARS) return 'the drawn signature is too large to keep; clear the pad and sign again';
  const payload = image.slice(SIGNATURE_IMAGE_PREFIX.length);
  if (!BASE64_PAYLOAD.test(payload) || !payload.startsWith(SIGNATURE_PNG_BASE64_MAGIC)) return 'the signature is drawn on the pad';
  return null;
}

/**
 * 🔒 What the lead's step of Complete the Visit sends to read the sheet as it will print, before the pad opens (the
 * owner's 5b; piece 29, *the customer signs what they are shown*): the answers the sheet prints, unsaved and unsigned.
 */
export type ServiceReportPreviewInput = Pick<CompleteVisitInput, 'outcome' | 'outcomeNote' | 'workSummary' | 'customerComment' | 'lineWork'>;

/** Who signs for the customer: their drawn signature, or the reason standing in its place. */
export type CustomerChoice = 'Signed' | VisitNoSignatureReason;

/** The completion sheet's draft, keyed to the visit it was opened on; the web's page and the phone's keep the same one. */
export interface Sheet {
  outcome: VisitOutcome;
  outcomeNote: string;
  workSummary: string;
  lineWork: Record<string, string>;
  customer: CustomerChoice;
  customerName: string;
  customerComment: string;
  customerImage: string | null;
  leadName: string;
  leadImage: string | null;
}

/** The sheet as it opens: the outcome Completed, the work already written on each line, and the lead's own name. */
export function sheetOf(answer: VisitPageAnswer): Sheet {
  return {
    outcome: 'Completed',
    outcomeNote: '',
    workSummary: '',
    lineWork: Object.fromEntries(answer.lines.map((row) => [row.lineId, row.workPerformed ?? ''])),
    customer: 'Signed',
    customerName: '',
    customerComment: '',
    customerImage: null,
    leadName: answer.crew.find((person) => person.isLead)?.name ?? '',
    leadImage: null,
  };
}

/** 🔒 The owner's 5b: what the lead's step prints, unsaved; the customer's comment travels as typed, since who signs is chosen beneath the sheet. */
export function previewOf(answer: VisitPageAnswer, sheet: Sheet): ServiceReportPreviewInput {
  return {
    outcome: sheet.outcome,
    outcomeNote: sheet.outcome === 'CouldNotComplete' ? sheet.outcomeNote : null,
    workSummary: sheet.workSummary,
    customerComment: sheet.customerComment,
    lineWork: answer.lines.map((row) => ({ lineId: row.lineId, workPerformed: sheet.lineWork[row.lineId] ?? '' })),
  };
}

/** The sheet as it will print, the printer's own HTML for the sheet frame with both signature boxes empty, and its page. */
export interface ServiceReportPreviewAnswer {
  printSheet: PrintSheetHtml | null;
  printLayout: PrintSheetLayout | null;
}

/**
 * 🔒 Why the lead's step is refused, or null where the sheet is drawn (the owner's 5b): the visit's lead alone, on a
 * started visit, the outcome chosen, and a visit that could not complete says why. Complete the Visit asks the same
 * first, then the signatures.
 */
export function previewVisitProblem(facts: { status: WorkOrderVisitStatus; viewerIsLead: boolean }, input: Partial<ServiceReportPreviewInput> | null | undefined): string | null {
  if (!facts.viewerIsLead) return 'only the visit’s lead completes it';
  if (facts.status === 'Complete') return 'this visit is already complete';
  if (facts.status === 'Cancelled') return 'this visit was canceled';
  if (facts.status !== 'InProgress') return 'a visit is completed once it has started; tap Arrived or Clock In on it first';
  if (!input) return 'the completion sheet is empty';
  if (!input.outcome || !(VISIT_OUTCOMES as readonly string[]).includes(input.outcome)) return 'the visit completed, or could not complete';
  if (input.outcome === 'CouldNotComplete' && !input.outcomeNote?.trim()) return 'a visit that could not complete says why';
  return null;
}

/**
 * 🔒 Why Complete the Visit is refused, or null where it goes ahead (decisions
 * 1901, 1910, 2061). Only the visit's lead completes it, once it has started.
 * The customer signs or a reason stands in their place, and the lead signs
 * after them; a visit that could not complete says why.
 */
export function completeVisitProblem(facts: { status: WorkOrderVisitStatus; viewerIsLead: boolean }, input: Partial<CompleteVisitInput> | null | undefined): string | null {
  const first = previewVisitProblem(facts, input);
  if (first) return first;
  if (!input) return 'the completion sheet is empty';
  const reason = input.noSignatureReason ?? null;
  if (reason !== null && !(VISIT_NO_SIGNATURE_REASONS as readonly string[]).includes(reason)) return 'a missing signature is Nobody on site or Declined to sign';
  const customer = input.customerSignature ?? null;
  if (customer === null && reason === null) return 'the customer signs, or the sheet says Nobody on site or Declined to sign';
  if (customer !== null && reason !== null) return 'the customer signed, so no reason stands in place of their signature';
  if (reason === 'NobodyOnSite' && input.customerComment?.trim()) return 'nobody was on site to leave a comment';
  if (customer !== null) {
    if (!customer.name?.trim()) return 'the customer types their name beside their signature';
    const drawn = signatureImageProblem(customer.image);
    if (drawn) return `the customer’s signature: ${drawn}`;
  }
  const lead = input.leadSignature ?? null;
  if (!lead) return 'the lead signs the sheet after the customer';
  if (!lead.name?.trim()) return 'the lead types their name beside their signature';
  const leadDrawn = signatureImageProblem(lead.image);
  if (leadDrawn) return `the lead’s signature: ${leadDrawn}`;
  return null;
}

/** The sheet's title, the job's number and the visit's, as the frozen file is named. */
export function serviceReportFilename(workOrderNumber: string, visitLabel: string): string {
  return `${workOrderNumber}-${visitLabel}-service-report.pdf`;
}
